import {
  Box,
  IconButton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import { AdminRestaurantSubscriptionDto } from "src/data/admin/types/subscription";
import { formatRestaurantLabel } from "src/business/restaurant/restaurantLabel";
import { TablePaginationBar } from "src/design/TablePaginationBar";
import { usePagination } from "src/helpers/usePagination";
import {
  colorActionDanger,
  colorSurfaceDangerHover,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";
import { SubscriptionStatusChip } from "./SubscriptionStatusChip";

/**
 * A subscription plus the city of its restaurant, which the admin endpoint does
 * not carry — it is joined in from the restaurant list.
 */
export type SubscriptionRow = AdminRestaurantSubscriptionDto & {
  city?: string;
};

interface SubscriptionsTableProps {
  subscriptions: SubscriptionRow[];
  onEdit: (subscription: AdminRestaurantSubscriptionDto) => void;
  onRenew: (subscription: AdminRestaurantSubscriptionDto) => void;
  onRevoke: (subscription: AdminRestaurantSubscriptionDto) => void;
  sx?: SxProps;
}

const formatDate = (date: string | null) =>
  date ? dayjs(date).format("MMM D, YYYY") : "—";

const headerCellSx = { color: colorTextMuted, fontWeight: 600 } as const;

/** The subscriptions on record, one row per restaurant, with the ops actions. */
export const SubscriptionsTable = ({
  subscriptions,
  onEdit,
  onRenew,
  onRevoke,
  sx,
}: SubscriptionsTableProps) => {
  const { page, rowsPerPage, visibleRows, setPage, setRowsPerPage } =
    usePagination(subscriptions);

  return (
    <Box sx={sx}>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ mt: -1, minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, pl: 0 }}>Restaurant</TableCell>
              <TableCell sx={headerCellSx}>Tier</TableCell>
              <TableCell sx={headerCellSx}>Status</TableCell>
              <TableCell sx={headerCellSx}>Expires on</TableCell>
              <TableCell sx={headerCellSx}>Access ends on</TableCell>
              <TableCell sx={headerCellSx}>Auto-renewal</TableCell>
              <TableCell align="right" sx={{ pr: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((subscription) => (
              <TableRow
                key={subscription.restaurantId}
                sx={{
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <TableCell sx={{ pl: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: colorTextPrimary }}
                  >
                    {formatRestaurantLabel(
                      subscription.restaurantName,
                      subscription.city,
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colorTextMuted, fontFamily: "monospace" }}
                  >
                    {subscription.restaurantId}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: colorTextPrimary }}>
                  {subscription.tier}
                </TableCell>
                <TableCell>
                  <SubscriptionStatusChip subscription={subscription} />
                </TableCell>
                <TableCell sx={{ color: colorTextMuted }}>
                  {formatDate(subscription.nextBillingCycle)}
                </TableCell>
                <TableCell sx={{ color: colorTextMuted }}>
                  {formatDate(subscription.accessEndsOn)}
                </TableCell>
                <TableCell sx={{ color: colorTextMuted }}>
                  {subscription.autoRenewal ? "On" : "Off"}
                </TableCell>
                <TableCell align="right" sx={{ pr: 0, whiteSpace: "nowrap" }}>
                  <Tooltip title="Renew">
                    <IconButton
                      size="small"
                      onClick={() => onRenew(subscription)}
                      sx={{ color: colorTextMuted }}
                    >
                      <AutorenewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Update tier or expiration">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(subscription)}
                      sx={{ color: colorTextMuted }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Revoke">
                    <IconButton
                      size="small"
                      onClick={() => onRevoke(subscription)}
                      sx={{
                        color: colorTextMuted,
                        "&:hover": {
                          color: colorActionDanger,
                          backgroundColor: colorSurfaceDangerHover,
                        },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <TablePaginationBar
        count={subscriptions.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />
    </Box>
  );
};
